import * as amplitude from "@amplitude/analytics-react-native"
import { theme } from "@common/theme/theme"
import { resetFormData, saveFormData } from "@data/redux/actions/formData"
import moment from "moment"
import React, { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { View } from "react-native"
import { useDispatch, useSelector } from "react-redux"
import { SnackBarM } from "../alert/snackbar/SnackBar"
import { FormStep } from "./FormStep"
import { formStepName } from "./formStepName"
import ProgressStepper from "./ProgressStepper"

export const FormStepper = (props) => {
    // ** Component props
    const {
        navigation,
        formData,
        champion,
        formValues,
        setFormValues,
        onSubmit,
        submitted,
        submittedMessage,
        circularProgress,
        circularProgressValue,
        serverError,
        hideProgressBar = false,
        resetsToDefaultOnNext,
        previousRouteName = "menu",
        track = false,
        submitButtonText = "Terminer",
        submitButtonColor = theme.colors.success,
        noPrevious = false,
        noGoBack = false,
        exitHandler = () => {},
        exitWarning = false,
        exitWarningMessage = "Les données entrées seront perdues.",
        hideGoBack = false,
        noNavigation = false,
        flex = 1,
        additionalDefaultValues = {},
        formId,
        entryId,
        syncFormValues = false,
        getGoToNextStep,
        getGoToStep,
    } = props

    // Use useSelector to get form data from Redux
    const reduxFormData = useSelector((state) =>
        formId && entryId ? state.data?.formData?.[formId]?.[entryId] ?? {} : {}
    )

    const defaultValues = {
        ...additionalDefaultValues,
        ...formData?.reduce((pValue, cValue) => {
            if (cValue?.name) {
                const nValue = { ...pValue }
                nValue[cValue?.name] = cValue?.defaultValue ?? null
                return nValue
            } else if (cValue?.inputs) {
                const nValue = { ...pValue }

                cValue?.inputs?.forEach((i) => {
                    nValue[i?.name] = i?.defaultValue ?? null
                })

                if (cValue?.checkBoxes) {
                    cValue?.checkBoxes?.forEach((cb) => {
                        nValue[cb?.name] = cb?.defaultValue ?? null
                    })
                }
                return nValue
            }
        }, {}),
        // Fetch saved data from Redux based on formId and entryId
        ...(reduxFormData ? reduxFormData : {}),
    }

    useEffect(() => {
        if (!formValues) setFormValues(defaultValues)
        if (formId && entryId && Object.keys(reduxFormData)?.length > 0) {
            SnackBarM.show({
                text1: `↪️ Dernières données de formulaire restaurées`,
                duration: 4000,
            })
        }
    }, [])

    const {
        control,
        handleSubmit,
        formState: { errors, dirtyFields, isDirty },
        trigger,
        register,
        setError,
        reset,
    } = useForm({
        defaultValues: defaultValues,
    })

    // New effect to update useForm values when formValues prop changes
    useEffect(() => {
        if (syncFormValues && formValues) {
            // Update the form with the new values from formValues
            reset(formValues)
        }
    }, [formValues, syncFormValues])

    const dispatch = useDispatch()

    const [currentStep, setCurrentStep] = useState(1)
    const isLastStep = currentStep === formData?.length

    useEffect(() => {
        if (formData?.[currentStep - 1]?.onReachStep) {
            formData?.[currentStep - 1]?.onReachStep()
        }
    }, [currentStep])

    const goToNextStep = async (
        { skipStepCustomAction } = {
            skipStepCustomAction: false,
        }
    ) => {
        if (track)
            amplitude.track("form.step.finished", {
                step: formStepName(formData?.[currentStep - 1]),
            })

        const currentStepData = formData?.[currentStep - 1]

        // Conditionally save form data based on formId and entryId
        if (formId && entryId) {
            dispatch(saveFormData(formId, entryId, control._formValues))
        }

        if (currentStepData?.onNextButtonPress && !skipStepCustomAction) {
            currentStepData.onNextButtonPress()
            if (!currentStepData?.continueAfterCustomAction) return
        }

        if (!isLastStep) {
            // Update form values of parent screen to update form data accordingly
            const toValidate =
                currentStepData?.validationRules?.validateOnNext ??
                currentStepData?.validationRules?.customCondition
            if (toValidate && toValidate.message) {
                const value = control._formValues?.[currentStepData.name]
                if (!toValidate.value(value)) {
                    setError(currentStepData.name, {
                        type: "custom",
                        message: toValidate.message,
                    })
                    return
                }
            }
            const result = await trigger()

            if (result) {
                setFormValues({ ...control._formValues })
                setCurrentStep((cs) => cs + 1)
            }
            return
        }
        await handleSubmit(onSubmit)()

        // Conditionally reset form data after submission
        if (formId && entryId) {
            dispatch(resetFormData(formId, entryId))
        }
    }
    useEffect(() => {
        if (getGoToNextStep) getGoToNextStep(goToNextStep)
        if (getGoToStep) getGoToStep(setCurrentStep)
    }, [getGoToNextStep, getGoToStep, currentStep])

    return (
        <View
            style={{
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
            }}
        >
            <ProgressStepper
                navigation={navigation}
                noNavigation={noNavigation}
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                track={track}
                noPrevious={noPrevious}
                noGoBack={noGoBack}
                hideGoBack={hideGoBack}
                exitHandler={exitHandler}
                exitWarning={(!noNavigation && isDirty) || exitWarning}
                exitWarningMessage={exitWarningMessage}
                hideProgressBar={hideProgressBar}
                onNextButtonPress={goToNextStep}
                champion={champion}
                champions={formData?.map((x) => {
                    if (x?.champion) {
                        return x?.champion
                    } else return null
                })}
                championLabels={formData?.map((x) => x?.label)}
                submitted={submitted}
                circularProgress={circularProgress}
                circularProgressValue={circularProgressValue}
                submittedMessage={submittedMessage}
                previousRouteName={previousRouteName}
                submitButtonText={submitButtonText}
                submitButtonColor={submitButtonColor}
                flex={flex}
                formData={formData}
                steps={formData?.map((x, index) => {
                    const isRequired = (formDataItem) => {
                        if (formDataItem?.type === "text.multiple") {
                            return !!formDataItem?.validationRules?.required
                        } else {
                            return !!formDataItem?.inputs?.reduce(
                                (prev, cur) =>
                                    prev && !!cur?.validationRules?.required,
                                true
                            )
                        }
                    }
                    const customCondition = (formDataItem) => {
                        return !!formDataItem?.validationRules?.customCondition
                    }
                    const isMinLength = (formDataItem) => {
                        if (formDataItem?.type === "select.multiple") {
                            return !!formDataItem?.validationRules?.minLength
                        }
                        return false
                    }

                    const isEmpty = (formDataItem) => {
                        if (formDataItem?.type === "select.multiple") {
                            return !control._formValues?.[formDataItem?.name]
                                ?.length
                        } else if (formDataItem?.type === "select.single") {
                            return (
                                control._formValues?.[formDataItem?.name] ===
                                    null ||
                                control._formValues?.[formDataItem?.name] ===
                                    undefined
                            )
                        } else if (formDataItem?.type === "photos") {
                            return (
                                control._formValues?.[formDataItem?.name]
                                    ?.file_keys?.length === 0
                            )
                        }

                        return false
                    }

                    const isStepDirty =
                        (x.type === "text.multiple" &&
                            x.inputs?.reduce(
                                (pValue, cValue) =>
                                    pValue || dirtyFields[cValue.name],
                                false
                            )) ||
                        (x.type === "text.single" && dirtyFields[x.name])

                    const hideNextButton =
                        !x?.showNextButton &&
                        ((isRequired(x) &&
                            x.type === "text.multiple" &&
                            x.inputs?.reduce(
                                (pValue, cValue) =>
                                    pValue || !dirtyFields[cValue.name],
                                false
                            )) ||
                            (x.type === "select.multiple" && isEmpty(x)) ||
                            (x.type === "select.single" && isEmpty(x)) ||
                            //TODO : delete this deprecated case (use text.multiple instead)
                            (x.type === "text.single" &&
                                !dirtyFields[x.name]) ||
                            (x.type === "photos" && !dirtyFields[x.name]) ||
                            (x.type === "day-time" &&
                                !moment(
                                    control._formValues?.[x.name],
                                    "dddd HH:mm"
                                ).isValid()) ||
                            (x.type === "date-time" &&
                                !moment(
                                    control._formValues?.[x.name],
                                    "YYYY-MM-DD HH:mm",
                                    true
                                ).isValid()) ||
                            (isMinLength(x) &&
                                control._formValues?.[x.name]?.length <
                                    x?.validationRules?.minLength?.value) ||
                            (customCondition(x) &&
                                x?.validationRules?.customCondition?.value) ||
                            (x.type === "custom" && !x?.showNextButton) ||
                            x.type === "select.two.oriented" ||
                            x.type === "select.card.single" ||
                            x.type === "select.grouped.single")

                    const hideSubmitButton =
                        x?.showSubmitButton === false && isLastStep

                    return {
                        content: (
                            <FormStep
                                key={index}
                                index={index}
                                track={track}
                                data={x}
                                setError={setError}
                                resetsToDefaultOnNext={resetsToDefaultOnNext}
                                goToNextStep={goToNextStep}
                                isStepDirty={isStepDirty}
                                trigger={trigger}
                                setFormValues={setFormValues}
                                register={register}
                                champion={x?.champion ?? champion}
                                control={control}
                                isLastStep={isLastStep}
                                serverError={serverError}
                                hideNextButton={hideNextButton}
                                errors={errors}
                                formValues={formValues}
                                flex={flex}
                            />
                        ),
                        hideNextButton,
                        hideSubmitButton,
                        nextButtonLoading: x?.nextButtonLoading,
                    }
                })}
            />
        </View>
    )
}
